import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import VittaISEPathways from '../components/VittaISEPathways';

const ISEStudyGuide = () => {
  // Book and Course data
  const studyMaterials = {
    book: {
      title: "Printed ISE Study Guide",
      subtitle: "Complete Study Book",
      description: "Comprehensive exam-focused study guide for the CIRO Investment Services Exam (ISE).",
      price: "299.99",
      link: "https://a.co/d/4QD7Dva"
    },
    interactiveBook: {
      title: "ISE Interactive Study Guide",
      description: "Master the ISE exam with interactive flashcards, quizzes, and visual learning tools across all 7 elements.",
      price: "49.99",
      link: "/books/ise-interactive"
    },
    course: {
      title: "ISE Exam Prep Course",
      price: "29.99",
      link: "https://vittafinance.mykajabi.com/offers/W6ezafud"
    }
  };

  const learningPath = [
    {
      phase: "Phase 1: Getting Started",
      description: "Understand the ISE exam basics and institutional marketplace",
      duration: "Week 1",
      articles: [
        {
          title: "ISE Exam Overview: Complete Guide",
          slug: "ise-exam-overview-complete-guide",
          description: "Exam structure, element weightings, and study strategy"
        },
        {
          title: "What is the CIRO Exam? Beginner's Guide",
          slug: "what-is-ciro-exam-beginners-guide",
          description: "Understanding CIRO exams and career paths"
        },
        {
          title: "RSE vs ISE: Which Exam Should You Take?",
          slug: "rse-vs-ise-exam-comparison",
          description: "Compare retail vs institutional career paths"
        }
      ]
    },
    {
      phase: "Phase 2: Element 1 - Institutional Client Relationships (16%)",
      description: "Master buy-side vs sell-side dynamics and institutional services",
      duration: "Week 2",
      articles: [
        {
          title: "Managing Institutional Client Relationships",
          slug: "ise-exam-institutional-client-relationships",
          description: "Buy-side, sell-side, trading desks, prime brokerage"
        }
      ],
      topics: [
        "Buy-Side vs. Sell-Side ecosystem",
        "Pension Funds and Liability-Driven Investing (LDI)",
        "Hedge Funds and Alpha generation",
        "Trading Desks: Agency, Principal, Prop, Market Makers",
        "Prime Brokerage services",
        "Securities Lending mechanics"
      ]
    },
    {
      phase: "Phase 3: Institutional Onboarding & Compliance",
      description: "Learn KYC for institutions, AML requirements, and information barriers",
      duration: "Week 2-3",
      articles: [
        {
          title: "Institutional KYC, AML & Onboarding",
          slug: "ise-exam-institutional-kyc-aml-onboarding",
          description: "Permitted Clients, DVP, UBO, Chinese Walls"
        }
      ],
      topics: [
        "Institutional Client and Permitted Client definitions",
        "DVP (Delivery vs. Payment) settlement",
        "Ultimate Beneficial Owner (UBO) identification",
        "Suitability Exemption and KYP requirements",
        "AML: Placement, Layering, Integration stages",
        "Information Barriers (Chinese Walls)",
        "Grey List and Restricted List"
      ]
    },
    {
      phase: "Phase 4: Element 2 - Conflicts & Professional Conduct (8%)",
      description: "Ethics, CFR framework, and prohibited practices",
      duration: "Week 3",
      articles: [
        {
          title: "Conflicts of Interest & Professional Conduct",
          slug: "ise-exam-conflicts-interest-professional-conduct",
          description: "Rule 1400, CFR framework, front running, churning"
        }
      ],
      topics: [
        "Rule 1400: The Fundamental Duty",
        "CFR Framework: Identify → Address → Disclose",
        "Front Running and Tailgating",
        "Churning and Bucketing",
        "Personal Financial Dealings prohibition",
        "Client Priority Rule"
      ]
    },
    {
      phase: "Phase 5: Element 3 - Fixed Income (12%)",
      description: "OTC bond market, yields, duration, and institutional strategies",
      duration: "Week 3-4",
      articles: [
        {
          title: "Fixed Income for Institutional Markets",
          slug: "ise-exam-fixed-income-institutional",
          description: "YTM, duration, convexity, credit spreads"
        }
      ],
      topics: [
        "OTC Dealer Market structure",
        "Inter-Dealer Brokers (IDBs)",
        "YTM vs. Current Yield calculations",
        "Modified Duration and Convexity",
        "Clean Price vs. Dirty Price",
        "Credit Spreads and Yield Curve",
        "Immunization strategy"
      ]
    },
    {
      phase: "Phase 6: Element 4 - Equities (13%)",
      description: "Corporate structures, prospectus requirements, and exempt market",
      duration: "Week 4",
      articles: [
        {
          title: "Equities & Corporate Finance",
          slug: "ise-exam-equities-corporate-finance",
          description: "Prospectus, exempt market, dual-class shares"
        }
      ],
      topics: [
        "Corporate structures and limited liability",
        "NI 41-101 Prospectus requirements",
        "Preliminary Prospectus (Red Herring)",
        "Exempt Market (NI 45-106)",
        "Accredited Investor exemption",
        "Primary vs. Secondary distributions",
        "Dual-Class Shares and Coattail provisions",
        "NVCC Bail-In securities"
      ]
    },
    {
      phase: "Phase 7: Element 5 Part 1 - Securities Valuation (31%)",
      description: "DCF, DDM, financial statement analysis, and valuation ratios",
      duration: "Week 5-6",
      articles: [
        {
          title: "Securities Valuation: DCF, DDM & Ratios",
          slug: "ise-exam-securities-valuation-dcf-ddm",
          description: "Intrinsic value, P/E, ROE, financial analysis"
        }
      ],
      highlight: true,
      topics: [
        "Intrinsic Value concept",
        "Discounted Cash Flow (DCF) model",
        "Dividend Discount Model (DDM): P = D₁/(k-g)",
        "Top-Down vs. Bottom-Up analysis",
        "Growth vs. Value investing styles",
        "Liquidity Ratios: Current, Quick",
        "Profitability: ROE, EPS, Payout Ratio",
        "Valuation: P/E, Price-to-Book"
      ]
    },
    {
      phase: "Phase 8: Element 5 Part 2 - Portfolio Theory (31%)",
      description: "CAPM, MPT, Alpha, Beta, Sharpe Ratio, and risk management",
      duration: "Week 6-7",
      articles: [
        {
          title: "Portfolio Theory: CAPM, MPT & Risk Metrics",
          slug: "ise-exam-portfolio-theory-capm-mpt-risk",
          description: "Alpha, Beta, Sharpe, Efficient Frontier"
        }
      ],
      highlight: true,
      topics: [
        "Modern Portfolio Theory (MPT)",
        "Efficient Frontier",
        "CAPM Formula: E(R) = Rf + β[Rm - Rf]",
        "Alpha (excess return) and Beta (systematic risk)",
        "Standard Deviation (total risk)",
        "Sharpe Ratio and Treynor Ratio",
        "Systematic vs. Unsystematic risk",
        "SAA vs. TAA allocation",
        "Efficient Market Hypothesis (EMH)"
      ]
    },
    {
      phase: "Phase 9: Element 6 - Managed & Alternative Products (8%)",
      description: "ETFs, derivatives, private equity, and cryptocurrency",
      duration: "Week 7-8",
      articles: [
        {
          title: "Managed Products, ETFs & Alternatives",
          slug: "ise-exam-managed-products-etfs-alternatives",
          description: "Options, futures, PE, ETF mechanics, crypto"
        }
      ],
      topics: [
        "REITs and Income Trusts",
        "Call and Put options",
        "Futures vs. Forwards",
        "Clearinghouse and counterparty risk",
        "ETF Creation/Redemption (In-Kind)",
        "Leveraged ETF volatility decay",
        "Private Equity and LBOs",
        "Cryptocurrency and custody risk"
      ]
    },
    {
      phase: "Phase 10: Element 7 - Execution & Market Integrity (12%)",
      description: "Best execution, order types, dark pools, and trading mechanics",
      duration: "Week 8",
      articles: [
        {
          title: "Execution, Market Integrity & Trading",
          slug: "ise-exam-execution-market-integrity-trading",
          description: "UMIR, dark pools, algorithmic trading, T+1"
        }
      ],
      topics: [
        "UMIR (Universal Market Integrity Rules)",
        "Best Execution obligation",
        "Client Priority Rule",
        "Order Types: Limit, Market, Stop, Iceberg, VWAP",
        "Lit Markets vs. Dark Pools",
        "Algorithmic and High-Frequency Trading",
        "T+1 Settlement rules",
        "Wash Trading and Marking the Close"
      ]
    },
    {
      phase: "Phase 11: Final Review & Practice",
      description: "Mock exams, formulas review, and exam preparation",
      duration: "Week 9-10",
      articles: [
        {
          title: "Free CIRO Practice Questions",
          slug: "free-ciro-practice-questions",
          description: "Test your knowledge before exam day"
        }
      ],
      topics: [
        "CAPM formula memorization",
        "Key ratios: P/E, ROE, Current, Quick",
        "Duration and yield calculations",
        "Alpha, Beta, Sharpe definitions",
        "Settlement rules and order types",
        "Prohibited practices identification"
      ]
    }
  ];

  const additionalResources = [
    {
      title: "CIRO Exam Requirements & Eligibility",
      slug: "ciro-exam-requirements-eligibility",
      description: "Registration and prerequisites"
    },
    {
      title: "CIRO Exam Cost & Fees Guide",
      slug: "ciro-exam-cost-fees-guide",
      description: "Complete pricing breakdown"
    },
    {
      title: "CIRO Exam Pass Rates & Difficulty",
      slug: "ciro-exam-pass-rates-difficulty",
      description: "What to expect on the exam"
    },
    {
      title: "Investment Career Path in Canada",
      slug: "investment-career-path-canada",
      description: "From entry to senior advisor"
    },
    {
      title: "Financial Services Career Roadmap",
      slug: "financial-services-career-roadmap-blueprint",
      description: "Complete career blueprint"
    }
  ];

  return (
    <>
      <SEO 
        title="ISE Exam Study Guide | Free Learning Path for Institutional Securities Exam | Vitta Finance Academy"
        description="Master the Institutional Securities Exam (ISE) with our structured 10-week study guide covering all 7 elements. Free learning path with CAPM, portfolio theory, institutional client relationships, fixed income, and execution. Pass ISE on your first attempt."
        keywords="ISE study guide, ISE exam preparation, Institutional Securities Exam learning path, ISE exam topics, ISE free resources, CAPM portfolio theory, ISE element 5, institutional client relationships"
      />
      
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)',
        padding: '80px 20px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <span style={{
            background: 'rgba(212, 175, 55, 0.2)',
            color: '#d4af37',
            padding: '8px 20px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '600',
            display: 'inline-block',
            marginBottom: '20px'
          }}>
            FREE STUDY RESOURCE
          </span>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '800',
            marginBottom: '20px',
            lineHeight: '1.2'
          }}>
            ISE Exam Study Guide
          </h1>
          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            opacity: '0.9',
            maxWidth: '700px',
            margin: '0 auto 30px',
            lineHeight: '1.7'
          }}>
            Your complete 10-week learning path to pass the Institutional Securities Exam. 
            Master CAPM, portfolio theory, institutional client relationships, and all 7 elements.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/ise-exam"
              data-testid="ise-course-btn"
              style={{
                background: '#d4af37',
                color: '#1e3a8a',
                padding: '14px 28px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              View ISE Course
            </Link>
            <Link 
              to="/books"
              data-testid="study-materials-btn"
              style={{
                background: 'transparent',
                color: 'white',
                padding: '14px 28px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                border: '2px solid white'
              }}
            >
              Get Study Materials
            </Link>
          </div>
        </div>
      </section>

      {/* Exam Overview */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2rem', color: '#1e3a8a', marginBottom: '40px' }}>
            ISE Exam at a Glance
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '20px'
          }}>
            {[
              { label: 'Questions', value: '100', sublabel: 'Multiple Choice' },
              { label: 'Duration', value: '3 Hours', sublabel: 'Computer-Based' },
              { label: 'Passing Score', value: 'Set by CIRO', sublabel: 'Per Sitting' },
              { label: 'Study Time', value: '8-10 Weeks', sublabel: 'Recommended' },
              { label: 'Elements', value: '7 Topics', sublabel: 'Institutional Focus' },
              { label: 'Largest Section', value: '31%', sublabel: 'Securities Analysis' }
            ].map((stat, index) => (
              <div key={index} style={{
                background: 'white',
                padding: '25px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
              }}>
                <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#1e3a8a' }}>{stat.value}</div>
                <div style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginTop: '5px' }}>{stat.label}</div>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '3px' }}>{stat.sublabel}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Element Weightings */}
      <section style={{ padding: '60px 20px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', color: '#1e3a8a', marginBottom: '30px' }}>
            ISE Exam Element Weightings
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px'
          }}>
            {[
              { element: 'Element 5', name: 'Securities Analysis & Investment Theory', weight: '31%', questions: '~31', priority: 'high' },
              { element: 'Element 1', name: 'Institutional Client Relationships', weight: '16%', questions: '~16', priority: 'medium' },
              { element: 'Element 4', name: 'Equities', weight: '13%', questions: '~13', priority: 'medium' },
              { element: 'Element 3', name: 'Fixed Income', weight: '12%', questions: '~12', priority: 'medium' },
              { element: 'Element 7', name: 'Execution & Market Integrity', weight: '12%', questions: '~12', priority: 'medium' },
              { element: 'Element 2', name: 'Conflicts & Standards', weight: '8%', questions: '~8', priority: 'low' },
              { element: 'Element 6', name: 'Managed Products', weight: '8%', questions: '~8', priority: 'low' }
            ].map((el, index) => (
              <div key={index} style={{
                background: el.priority === 'high' ? 'linear-gradient(135deg, #d4af37 0%, #b8972e 100%)' : 'white',
                color: el.priority === 'high' ? '#1e3a8a' : '#1e3a8a',
                padding: '18px',
                borderRadius: '10px',
                border: el.priority === 'high' ? 'none' : '1px solid #e5e7eb',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>{el.weight}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '2px' }}>{el.questions} questions</div>
                <div style={{ 
                  fontSize: '0.85rem', 
                  fontWeight: '600', 
                  marginTop: '10px',
                  borderTop: `1px solid ${el.priority === 'high' ? 'rgba(30,58,138,0.2)' : '#e5e7eb'}`,
                  paddingTop: '10px'
                }}>
                  {el.name}
                </div>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', color: '#6b7280', marginTop: '20px', fontSize: '0.9rem' }}>
            <strong>Focus on Element 5</strong> - it accounts for nearly one-third of the exam
          </p>
        </div>
      </section>

      {/* Learning Path */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2rem', color: '#1e3a8a', marginBottom: '15px' }}>
            Structured Learning Path
          </h2>
          <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '50px', maxWidth: '600px', margin: '0 auto 50px' }}>
            Follow this 11-phase study plan to systematically master all ISE exam content
          </p>

          {learningPath.map((phase, phaseIndex) => (
            <div key={phaseIndex} style={{ marginBottom: '35px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '15px',
                marginBottom: '15px'
              }}>
                <div style={{
                  width: '45px',
                  height: '45px',
                  borderRadius: '50%',
                  background: phase.highlight 
                    ? 'linear-gradient(135deg, #d4af37 0%, #b8972e 100%)'
                    : 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                  color: phase.highlight ? '#1e3a8a' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  flexShrink: 0
                }}>
                  {phaseIndex + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', margin: '0 0 3px' }}>{phase.phase}</h3>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>
                    {phase.description} • <span style={{ color: '#d4af37', fontWeight: '600' }}>{phase.duration}</span>
                  </p>
                </div>
              </div>

              <div style={{
                marginLeft: '22px',
                borderLeft: phaseIndex < learningPath.length - 1 ? '2px solid #e5e7eb' : 'none',
                paddingLeft: '35px',
                paddingBottom: phaseIndex < learningPath.length - 1 ? '15px' : '0'
              }}>
                {/* Articles */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: phase.articles.length > 1 ? 'repeat(auto-fit, minmax(250px, 1fr))' : '1fr',
                  gap: '12px',
                  marginBottom: phase.topics ? '15px' : '0'
                }}>
                  {phase.articles.map((article, articleIndex) => (
                    <Link 
                      key={articleIndex}
                      to={`/blog/${article.slug}`}
                      data-testid={`article-${article.slug}`}
                      style={{
                        background: 'white',
                        padding: '16px 18px',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        border: phase.highlight ? '2px solid #d4af37' : '1px solid #e5e7eb',
                        transition: 'all 0.2s ease',
                        display: 'block'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = '#1e3a8a';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(30, 58, 138, 0.1)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = phase.highlight ? '#d4af37' : '#e5e7eb';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <h4 style={{ 
                        fontSize: '0.95rem', 
                        color: '#1e3a8a', 
                        margin: '0 0 6px',
                        lineHeight: '1.4'
                      }}>
                        {article.title}
                      </h4>
                      <p style={{ 
                        fontSize: '0.8rem', 
                        color: '#6b7280', 
                        margin: 0,
                        lineHeight: '1.4'
                      }}>
                        {article.description}
                      </p>
                    </Link>
                  ))}
                </div>

                {/* Topics covered */}
                {phase.topics && (
                  <div style={{
                    background: 'white',
                    padding: '15px 18px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '10px', fontWeight: '600' }}>
                      Key Topics Covered:
                    </div>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {phase.topics.map((topic, topicIndex) => (
                        <span key={topicIndex} style={{
                          background: '#f1f5f9',
                          color: '#475569',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '0.75rem'
                        }}>
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Study Pathways */}
      <VittaISEPathways />

      {/* Additional Resources */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', color: '#1e3a8a', marginBottom: '30px' }}>
            Additional Resources
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '15px'
          }}>
            {additionalResources.map((resource, index) => (
              <Link 
                key={index}
                to={`/blog/${resource.slug}`}
                style={{
                  background: 'white',
                  padding: '18px 20px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'border-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = '#d4af37'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              >
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#d4af37',
                  flexShrink: 0
                }} />
                <div>
                  <div style={{ fontSize: '0.95rem', color: '#1e3a8a', fontWeight: '600' }}>{resource.title}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{resource.description}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Recommended Study Resources */}
      <section style={{ padding: '60px 20px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', color: '#1e3a8a', marginBottom: '30px' }}>
            Recommended Study Resources
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '25px'
          }}>
            {/* Book */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #d4af37',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: 'white',
                fontSize: '1.5rem'
              }}>
                📚
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '8px' }}>
                {studyMaterials.book.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                {studyMaterials.book.description}
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                  CAD ${studyMaterials.book.price}
                </span>
              </div>
              <a 
                href={studyMaterials.book.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: '#1e3a8a',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Buy on Amazon
              </a>
            </div>

            {/* Interactive Book */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #8b5cf6',
              textAlign: 'center',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '-10px',
                right: '20px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '700'
              }}>NEW</div>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: 'white',
                fontSize: '1.5rem'
              }}>
                ⚡
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '8px' }}>
                {studyMaterials.interactiveBook.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                {studyMaterials.interactiveBook.description}
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                  CAD ${studyMaterials.interactiveBook.price}
                </span>
              </div>
              <Link 
                to={studyMaterials.interactiveBook.link}
                style={{
                  display: 'inline-block',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Explore Interactive Book
              </Link>
            </div>

            {/* Course */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #1e3a8a',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #d4af37 0%, #b8972e 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: '#1e3a8a',
                fontSize: '1.5rem'
              }}>
                🎓
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '8px' }}>
                {studyMaterials.course.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                Video lessons, AI tutor, practice questions, mock exams, and expert instruction.
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                  CAD ${studyMaterials.course.price}
                </span>
                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>/month</span>
              </div>
              <a 
                href={studyMaterials.course.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: '#d4af37',
                  color: '#1e3a8a',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Enroll Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '15px' }}>Ready to Start Your ISE Journey?</h2>
          <p style={{ opacity: '0.9', marginBottom: '30px', fontSize: '1.1rem' }}>
            Get comprehensive prep with our ISE course featuring video lessons, practice questions, and full mock exams.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/ise-exam"
              data-testid="cta-ise-course"
              style={{
                background: '#d4af37',
                color: '#1e3a8a',
                padding: '16px 32px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1.1rem'
              }}
            >
              Explore ISE Course - $29.99/month
            </Link>
            <Link 
              to="/exam-comparison"
              style={{
                background: 'transparent',
                color: 'white',
                padding: '16px 32px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                border: '2px solid white'
              }}
            >
              Compare All CIRO Exams
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ISEStudyGuide;
